/**
 * Base API service with common Axios configuration and interceptors
 * All domain-specific API services extend this class
 */
import axios, { AxiosInstance, AxiosError } from 'axios';
import axiosRetry from 'axios-retry';
import {
  API_PATH,
  CONTENT_TYPE,
  APPLICATION_JSON,
  AUTHORIZATION,
  BEARER,
  TOKEN,
} from '../../constants';
import { errorHandler, ProcessedError } from '../utils/errorHandler';
import {
  ServiceResult,
  ServiceSuccessResult,
  ServiceErrorResult,
  ServiceError,
} from '../../types/common/api.types';

/** Error result that carries ProcessedError so callers can show correct errorType/details. */
export type ServiceErrorResultWithProcessed = ServiceErrorResult & {
  processedError: ProcessedError;
};

// Optional logger for retry events. Set when ARCHITECTURE §3 (logger abstraction) is available.
export type RetryLogger = { debug?: (message: string) => void };
let retryLogger: RetryLogger | undefined;
export function setRetryLogger(logger: RetryLogger | undefined): void {
  retryLogger = logger;
}

// Error handler options interface
export interface ErrorHandlerOptions {
  context?: string;
  showToast?: boolean;
  logError?: boolean;
}

export abstract class BaseApiService {
  protected api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_PATH,
      headers: {
        [CONTENT_TYPE]: APPLICATION_JSON,
      },
    });

    /**
     * Retries run at the Axios layer, so all calls through this instance (auth, trips, user, health)
     * get the same behavior. Axios-retry retries by default only for idempotent methods (ex: GET). Non-
     * idempotent methods (ex: POST, PATCH, DELETE) are not retried unless you override that. So create/
     * update/delete are not retried; list/get and similar reads are.
     */
    axiosRetry(this.api, {
      retries: 3, // Retry up to 3 times
      retryDelay: axiosRetry.exponentialDelay, // Exponential backoff
      retryCondition: (error: AxiosError) =>
        !error.response ||
        (error.response.status >= 500 && error.response.status < 600), // Retry on 5xx errors
      onRetry: (retryCount, error, config) => {
        retryLogger?.debug?.(`Retry ${retryCount} for ${config?.url}`);
      },
    });

    /**
     * Setup interceptors for the API
     * @returns The Axios instance
     */
    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Add request interceptor to include JWT token
    this.api.interceptors.request.use(
      config => {
        const token = localStorage.getItem(TOKEN);
        if (token) {
          config.headers[AUTHORIZATION] = `${BEARER} ${token}`;
        }
        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor to handle token expiration
    this.api.interceptors.response.use(
      response => response,
      error => {
        // Let errors propagate normally - no special handling needed
        return Promise.reject(error);
      }
    );
  }

  /**
   * Wraps API calls with error handling and returns ServiceResult
   * @param apiCall - The async API function to execute
   * @param options - Error handler options
   * @returns ServiceResult with success/error information
   */
  protected async withErrorHandling<T>(
    apiCall: () => Promise<T>,
    options?: ErrorHandlerOptions
  ): Promise<ServiceResult<T>> {
    try {
      const data = await apiCall();
      return this.createSuccessResult(data);
    } catch (error) {
      const processedError = errorHandler.handleError(
        error as AxiosError | Error,
        options
      );
      return this.createErrorResult(processedError);
    }
  }

  /**  Creates a success result  */
  protected createSuccessResult<T>(data: T): ServiceSuccessResult<T> {
    return {
      success: true,
      data,
    };
  }

  /** Creates an error result; when given ProcessedError, attaches it so unwrap can throw it. */
  protected createErrorResult(
    error: ProcessedError | Error | ServiceError
  ): ServiceErrorResult & { processedError?: ProcessedError } {
    let message = 'An unexpected error occurred';
    let details: Record<string, string> | undefined;

    if (error && typeof error === 'object' && 'errorType' in error) {
      const processed = error as ProcessedError;
      message = processed.message;
      details = processed.details;
      return {
        success: false,
        error: { message, details },
        message,
        details,
        processedError: processed,
      };
    }
    if (error && typeof error === 'object' && 'apiError' in error) {
      const apiError = (
        error as { apiError: { message?: string; details?: unknown } }
      ).apiError;
      message = apiError?.message ?? (error as Error).message ?? message;
      details = apiError?.details as Record<string, string>;
    } else if (error instanceof Error) {
      message = error.message;
    }

    return {
      success: false,
      error: { message, details },
      message,
      details,
    };
  }
}
